import { formatPhoneNumber } from '../common/Formating'
import { supabaseClient } from '../supabase'
import baseDataProvider from '../supabase/baseDataProvider'

// Cache slot_duration per center to avoid fetching on every getList
let slotDurationCache = { centerId: null, duration: null }

const getSlotDuration = async (centerId) => {
  if ( ! centerId ) return 30

  if ( slotDurationCache.centerId === centerId && slotDurationCache.duration !== null ) {
    return slotDurationCache.duration
  }

  try {
    const { data, error } = await supabaseClient
      .from('rendezvous_settings')
      .select('slot_duration')
      .eq('center_id', centerId)
      .single()

    if ( error || ! data ) return 30

    slotDurationCache = { centerId, duration: data.slot_duration ?? 30 }
    return slotDurationCache.duration
  } catch {
    return 30
  }
}

export const dataProvider = {
  ...baseDataProvider,
  getList: async (resource, params) => {
    const { filter = {}, pagination, sort } = params

    if ( ! filter ) {
      return baseDataProvider.getList(resource, params)
    }

    const { start_gte, start_lte, ...rest } = filter

    let query = supabaseClient
      .from(resource)
      .select(`
        *,
        leads(first_name, last_name, email, phone, assigned_pubeur, lead_pubeur:profiles!leads_assigned_pubeur_fkey(full_name)),
        profiles!assigned_pubeur(first_name, last_name)
      `, { count: 'exact' })

    // Calendar window filtering (ra-calendar sends start_gte/start_lte)
    if (start_gte && start_lte) {
      const fromISO = new Date(start_gte).toISOString()
      const toISO = new Date(start_lte).toISOString()

      // Filter by date_rdv for calendar view
      query = query
        .gte('date_rdv', fromISO)
        .lte('date_rdv', toISO)
    }

    // Apply other filters
    Object.entries(rest).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        query = query.eq(k, v)
      }
    })

    // Sort
    if (sort?.field) {
      query = query.order(sort.field, { ascending: sort.order !== 'DESC' })
    } else {
      query = query.order('date_rdv', { ascending: true })
    }

    // Paginate
    const page = pagination?.page || 1
    const perPage = pagination?.perPage || 25
    const from = (page - 1) * perPage
    const to = from + perPage - 1
    query = query.range(from, to)

    const { data, count, error } = await query

    if (error) {
      console.error('Error fetching rendezvous: ', error)
      return { data: [], total: 0 }
    }

    // Get slot_duration from the first record's center_id
    const centerId = data?.[0]?.center_id
    const slotDuration = await getSlotDuration(centerId)
    const durationMs = slotDuration * 60 * 1000

    const flattenedData = (data || []).map(record => {
      // Create start datetime
      let start = record.date_rdv
      if (record.time_rdv) {
        const dateOnly = record.date_rdv.split('T')[0]
        const timeOnly = record.time_rdv
        start = `${dateOnly}T${timeOnly}`
      }

      // Create end datetime based on configured slot duration
      const startDate = new Date(start)
      const endDate = new Date(startDate.getTime() + durationMs)
      const end = endDate.toISOString()

      // Create title
      const firstName = record.leads?.first_name || ''
      const lastName = record.leads?.last_name || ''
      const title = `${firstName} ${lastName}`.trim() || `RDV - ${record.type_rdv}`

      return {
        ...record,
        lead_first_name: record.leads?.first_name,
        lead_last_name: record.leads?.last_name,
        lead_email: record.leads?.email,
        lead_phone: formatPhoneNumber(record.leads?.phone, true),
        lead_assigned_pubeur_name: record.leads?.lead_pubeur?.full_name || null,
        assigned_pubeur_first_name: record.profiles?.first_name,
        assigned_pubeur_last_name: record.profiles?.last_name,
        // Calendar fields
        start,
        end,
        title
      }
    })

    return { data: flattenedData, total: count || 0 }
  }
}
